import {
  AdhocVariableToSet,
  Choice,
  ContentType,
  ContextVariableToSet,
  DiagnosticsActionResult,
  ErrorCodeMap,
  ErrorCodes,
  FormConstraintV2,
  FormFieldType,
  FormFieldV2,
  GraphNode,
  IContextMetadata,
  ISAFActionExecutionResponse,
  IUpdateAdhocVariablesParams,
  IVariableProvider,
  JsonDocument,
  LogEventName,
  LogLevel,
  Meta,
  NodeType,
  ParamOrVar,
  RegExpForParamOrVar,
  RegExpForTextContent,
  SAFActionProvider,
  Target,
  YodaContextVariablesKey
} from '../models';
import { YodaState } from '../rootReducer';
import { createImmutableList, createImmutableMap, IImmutableList, IImmutableMap } from '../state/redux';
import { resultCameraWithChoices } from '../test/sampleJsons/mock.payload';
import { IsUserSignedInHandlerType, LogHandlerType } from '../types';

const uuidv4 = require('uuid/v4');

/**
 *
 * @param text html string
 * use this method to set dangerously inner html for security pusposes
 * https://zhenyong.github.io/react/tips/dangerously-set-inner-html.html
 */
export const setDangerouslyInnerHtml = (text: string) => {
  return { __html: text };
};

/**
 * args - list of class names
 */
export const mergeClassNames = (...args: string[]): string => {
  if (args) {
    return args.join(' ');
  }
  return String();
};

/**function added to truncate string and add ... by checking length constraint **/
export const truncateWithEllipsis = (str: string, limit: number): string => {
  const ellipsis = ' ...';
  const space = ' ';
  if (!!str) {
    if (str.includes(space)) {
      /** sentence case */
      if (!!limit && str.length > limit) {
        const words: string[] = str.split(space);
        /** +4 for ellipsis */
        let wordsLength = 4;
        const truncatedWords = [];
        for (let i = 0; i < words.length; i++) {
          wordsLength += words[i].length;
          if (i > 0) {
            /** +1 for space */
            wordsLength += 1;
          }
          if (wordsLength < limit) {
            truncatedWords.push(words[i]);
          } else {
            break;
          }
        }
        if (truncatedWords.length > 0) {
          str = truncatedWords.join(space) + ellipsis;
        }
      }
    } else {
      /** handle case for single long word */
      if (str.length > limit) {
        return str.substring(0, limit) + ellipsis;
      }
    }
    return str;
  }
  return String();
};

export const uuidOptions = () => {
  // same logic as the uuid v3.3.2 for generating random seed using Math.random()
  const rnds = new Array(16);
  let r = Math.random() * 0x100000000;

  for (let i = 0; i < 16; i++) {
    // tslint:disable-next-line: no-bitwise
    if ((i & 0x03) === 0) {
      r = Math.random() * 0x100000000;
    }
    // tslint:disable-next-line: no-bitwise
    rnds[i] = (r >>> ((i & 0x03) << 3)) & 0xff;
  }

  return { random: rnds };
};

const _getNode = (uId: string, getState?: () => YodaState) => {
  let node;
  if (!!uId) {
    if (!!getState) {
      node = getState()
        .get('IntentGraph')
        .get('renderedNodes')
        .find((n: IImmutableMap<GraphNode>) => n.get('uId') === uId);
    }
  }
  return node;
};

export const getDialogInfo = (metadata: IImmutableMap<Meta>) => {
  if (!metadata) {
    return {};
  }
  return {
    dialogId: metadata.get('id') || '',
    dialogName: metadata.get('name') || '',
    parentDialogId: metadata.get('parentDialogId') || '',
    parentDialogName: metadata.get('parentDialogName') || '',
    dialogIdChain: metadata.get('dialogIdChain') || '',
    dialogTitle: metadata.get('title') || ''
  };
};

/**
 * @param metadata
 * @param getstate
 * @param nodeId
 * @param node
 *
 * @returns [id,title,supportAreaReference,nodeId,type,actionReference]
 */
// tslint:disable-next-line no-any
export const generateAdhocs = (
  metadata: IImmutableMap<Meta>,
  getState?: () => YodaState,
  uId?: string,
  node?: IImmutableMap<GraphNode>
): any[] => {
  const Adhocs = [];
  if (!!metadata) {
    const dialogInfo = getDialogInfo(metadata);
    Adhocs.push(metadata.get('id'));
    Adhocs.push(JSON.stringify(dialogInfo));
    Adhocs.push(metadata.get('supportAreaReference'));
  }

  uId = uId || (node && node.get('uId'));

  if (!node) {
    if (!!uId) {
      node = _getNode(uId, getState);
    }
  }

  if (!!node) {
    Adhocs.push(node.get('id'));
    Adhocs.push(node.get('type'));
    Adhocs.push(node.get('actionReference'));
    const parentUId = node.get('parentUId');
    Adhocs.push(getNodeLogProperties(parentUId, node, getState));
  }
  return Adhocs;
};

export const executeLogHandler = (
  logEventName: LogEventName,
  logLevel: LogLevel,
  logMessage: string = '',
  uId: string,
  dialogId: string,
  logHandler: LogHandlerType,
  getState: () => YodaState,
  adHocs?: any[]
) => {
  if (!!logHandler && typeof logHandler === 'function') {
    const jsonDocuments = getState()
      .get('IntentGraph')
      .get('staticConfig')
      .get('jsonDocuments');

    const jsonDocument = jsonDocuments.find(
      (jsonDoc: IImmutableMap<JsonDocument>) => jsonDoc.get('meta').get('id') === dialogId
    );

    const metadata = jsonDocument.get('meta');

    const sessionID = getState()
      .get('IntentGraph')
      .get('staticConfig')
      .get('sessionId');

    let nodeTags = null;
    if (!!uId) {
      const n = _getNode(uId, getState) as IImmutableMap<GraphNode>;
      if (!!n) {
        nodeTags = typeof n.get('tags') === 'string' ? n.get('tags') : JSON.stringify(nodeTags);
      }
    }

    const message = `message: ${logMessage}, tags: ${nodeTags}`;
    logHandler(logEventName, logLevel, sessionID, message, adHocs || generateAdhocs(metadata, getState, uId));
  }
};

export const _isElmInViewport = (elm: HTMLElement) => {
  const bounding = elm.getBoundingClientRect();
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const _notNullableObject = (obj: object) => {
  return typeof obj === 'object' && !(obj instanceof Array) && !(obj instanceof Date);
};

export const _isValidHTML = (html: string) => {
  let openingTags, closingTags;

  html = html.replace(/\r?\n|\r/g, ''); // remove new \n
  html = html.replace(/<[^>]*\/\s?>/g, ''); // Remove all self closing tags
  html = html.replace(/<(br|hr|img).*?>/g, ''); // Remove all <br>, <hr>, and <img> tags
  openingTags = html.match(/<[^\/].*?>/g) || []; // Get remaining opening tags
  closingTags = html.match(/<\/.+?>/g) || []; // Get remaining closing tags

  return openingTags.length === closingTags.length ? true : false;
};

export const _isTextOrHtmlValid = (textOrHtml: string) => {
  return textOrHtml && _isValidHTML(textOrHtml);
};

export const _nullableString = (obj: string) => {
  return !obj || typeof obj === 'string';
};

export const _notNullableString = (obj: string) => {
  return typeof obj === 'string';
};

export const _nullableNumber = (obj: number) => {
  return !obj || typeof obj === 'number';
};

export const _notNullableNumber = (obj: number) => {
  return typeof obj === 'number';
};

// tslint:disable-next-line: no-any
export const _nullableArray = (obj: any[]) => {
  return !obj || (typeof obj === 'object' && obj instanceof Array);
};

// tslint:disable-next-line: no-any
export const _notNullableArray = (obj: any[]) => {
  return typeof obj === 'object' && obj instanceof Array;
};

// tslint:disable-next-line: no-any
export const _notNullableContentType = (obj: ContentType) => {
  return !!obj && Object.values(ContentType).includes(obj);
};

export const _existsInNodes = (startNodeId: string, nodes: GraphNode[]) => {
  if (!startNodeId || typeof startNodeId !== 'string' || !nodes) {
    return false;
  }
  const node = nodes.find((n: GraphNode) => {
    return n && n.id && n.id.toLowerCase() === startNodeId.toLowerCase();
  });
  return !!node;
};

export const _nullableExistsInNodes = (startNodeId: string, nodes: GraphNode[]) => {
  return !startNodeId || _existsInNodes(startNodeId, nodes);
};

// tslint:disable-next-line: no-any
export const _ofPropType = (obj: any, type: any) => {
  const x =
    !!obj &&
    // basic type to type check, eg: type === "string"
    (typeof obj === type ||
      // basic value to value check, eg: 1 === 1
      obj === type ||
      // string case insensitive check
      (typeof type === 'string' && typeof obj === 'string' && obj.toLowerCase() && type.toLowerCase()) ||
      // string case insensitive check
      (typeof type === 'object' && Object.values(type).includes(obj.toLowerCase())) ||
      (typeof obj === 'object' && obj instanceof type));
  return x;
};

// tslint:disable-next-line: no-any
export const _nullableOfPropType = (obj: any, type: any) => {
  const x =
    !obj ||
    (!!obj &&
      // basic type to type check, eg: type === "string"
      (typeof obj === type ||
        // basic value to value check, eg: 1 === 1
        obj === type ||
        // string case insensitive check
        (typeof type === 'string' && typeof obj === 'string' && obj.toLowerCase() && type.toLowerCase()) ||
        // string case insensitive check across enum values
        (typeof type === 'object' &&
          Object.values(type)
            .join(',')
            .toLowerCase()
            .includes(obj.toLowerCase())) ||
        (typeof obj === 'object' && obj instanceof type)));
  return x;
};

export const _validateStaticChoices = (choices: Choice[], errors?: string[]) => {
  const baseErrorString = ``;
  if (choices) {
    choices.forEach((element: Choice, index: number) => {
      if (element) {
        // if(element.text)
      }
    });
  }
};

export const _hasRaiseEventName = (eventName: string) => {
  return eventName && eventName.toLowerCase() === 'ContactSupport'.toLowerCase();
};

/**
 * checks if array . delimiters are present
 * @param variable string to be checked
 * @returns boolean
 */
export const hasDotDelimiter = (variable: string) => {
  return variable && variable.includes('.');
};

/**
 * checks if array [] delimiters are present
 * @param variable string to be checked
 * @returns boolean
 */
export const hasArrayDelimiter = (variable: string) => {
  return variable && variable.includes('[') && variable.includes(']');
};

/**
 * checks string for array indexing and returns the values between [] as string[], added because regex lookbehind methods don't work in IE
 * @param variable string that might contain array indexing
 * @returns null or array of indexes
 */
export const readIndexesFromString = (variable: string): string[] | null => {
  const indexes = [];
  if (hasArrayDelimiter(variable)) {
    let variableIndex: string = '';
    let braceOpened = true;
    for (let idx = variable.indexOf('[') + 1; idx < variable.length; idx++) {
      if (variable[idx] === '[') {
        braceOpened = true;
      }
      if (variable[idx] !== '[' && variable[idx] !== ']' && braceOpened) {
        variableIndex += variable[idx];
      }
      if (variable[idx] === ']' && braceOpened && !!variableIndex) {
        indexes.push(variableIndex);
        variableIndex = '';
        braceOpened = false;
      }
    }
    return indexes && indexes.length > 0 ? indexes : null;
  }
  return null;
};

/**
 * Safely tries to parse, if exception caught it will return original value
 * @param value value to parsed
 * @returns parsed object or original value
 */
export const getJson = (value: string) => {
  if (value) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

/**
 * Perform case insensitive read on an object
 * @param source source object, could be string, array, serialized object, object
 * @param variable property name
 * @returns value found
 */
export const caseInsensitiveReadValue = (source: any, variable: string): any => {
  let result;
  if (typeof source === 'string') {
    source = getJson(source);
  }
  if (variable && source) {
    Object.keys(source).find(k => {
      if (!!k && k.trim() && k.toLowerCase() === variable.trim().toLowerCase()) {
        result = source[k];
        return true;
      }
      return false;
    });
  }
  return result;
};

/**
 * reads value using dot notation or [] indexing
 * @param variable string value for variable path, eg: Value.key1[index]...
 * @param source source object, could be string, array, serialized object, object
 * @returns null or found object
 */
export const readValue = (variable: string, source: any): any => {
  let result = null;
  let key = '';
  let indexOfFirstDotDelimiter = -1;
  let indexOfFirstArrayDelimiter = -1;
  if (typeof source === 'string') {
    source = getJson(source);
  }
  if (variable.startsWith('.') && variable.length > 1) {
    variable = variable.slice(1, variable.length);
  }
  if (variable && source) {
    result = caseInsensitiveReadValue(source, variable);
    if (result !== undefined) {
      return result;
    } else {
      // clean up quotes
      if (variable.includes("'") || variable.includes('"')) {
        variable = variable.replace("'", '').replace('"', '');
      }
      if (typeof variable === 'string') {
        if (hasDotDelimiter(variable)) {
          indexOfFirstDotDelimiter = variable.indexOf('.');
        }
        if (hasArrayDelimiter(variable)) {
          indexOfFirstArrayDelimiter = variable.indexOf('[');
        }
        if (
          (indexOfFirstDotDelimiter !== -1 &&
            indexOfFirstArrayDelimiter !== -1 &&
            indexOfFirstDotDelimiter < indexOfFirstArrayDelimiter) ||
          (indexOfFirstDotDelimiter !== -1 && indexOfFirstArrayDelimiter === -1)
        ) {
          key = variable.slice(0, indexOfFirstDotDelimiter);
          // +1 for .
          variable = variable.slice(variable.indexOf('.') + 1, variable.length);
        } else if (indexOfFirstArrayDelimiter !== -1) {
          if (variable.startsWith('[')) {
            const indexes = readIndexesFromString(variable);
            key = (indexes && indexes[0]) || '';
            if (key) {
              // slice starting from key.length + 2 for '[' and ']'
              variable = variable.slice(key.length + 2, variable.length);
            } else {
              console.log(['key is null for array index', variable]);
              return null;
            }
          } else {
            key = variable.slice(0, indexOfFirstArrayDelimiter);
            variable = variable.slice(variable.indexOf('['), variable.length);
          }
        }
        // replace quotes in keys with empty string
        key = key.replace("'", '').replace('"', '');
        result = caseInsensitiveReadValue(source, key);
        if (variable.length === 0 || !result) {
          return result;
        } else {
          return readValue(variable, result);
        }
      }
    }
  }
  return result;
};

/**
 * reads value using dot notation or [] indexing
 * @param variable string value for variable path, eg: Value.key1[index]...
 * @returns inital key and variable
 */
export const handleVariableIndex = (variable: string): any => {
  let key = '';
  let indexOfFirstDotDelimiter = -1;
  let indexOfFirstArrayDelimiter = -1;
  if (!!variable) {
    if (variable.startsWith('.') && variable.length > 1) {
      variable = variable.slice(1, variable.length);
    }
    // clean up quotes
    if (variable.includes("'") || variable.includes('"')) {
      variable = variable.replace("'", '').replace('"', '');
    }
    if (typeof variable === 'string') {
      if (hasDotDelimiter(variable)) {
        indexOfFirstDotDelimiter = variable.indexOf('.');
      }
      if (hasArrayDelimiter(variable)) {
        indexOfFirstArrayDelimiter = variable.indexOf('[');
      }
      if (
        (indexOfFirstDotDelimiter !== -1 &&
          indexOfFirstArrayDelimiter !== -1 &&
          indexOfFirstDotDelimiter < indexOfFirstArrayDelimiter) ||
        (indexOfFirstDotDelimiter !== -1 && indexOfFirstArrayDelimiter === -1)
      ) {
        key = variable.slice(0, indexOfFirstDotDelimiter);
        // +1 for .
        variable = variable.slice(variable.indexOf('.') + 1, variable.length);
      } else if (indexOfFirstArrayDelimiter !== -1) {
        if (variable.startsWith('[')) {
          const indexes = readIndexesFromString(variable);
          key = (indexes && indexes[0]) || '';
          if (key) {
            // slice starting from key.length + 2 for '[' and ']'
            variable = variable.slice(key.length + 2, variable.length);
          } else {
            console.log(['key is null for array index', variable]);
            return null;
          }
        } else {
          key = variable.slice(0, indexOfFirstArrayDelimiter);
          variable = variable.slice(variable.indexOf('['), variable.length);
        }
      } else {
        key = variable;
        variable = '';
      }
    }
  }
  return {
    key,
    variable
  };
};

export const readValueFromGlobalVars = (globalVars: IImmutableList<IImmutableMap<ParamOrVar>>, path: string): any => {
  //use readValue method with matched globalVars param being the source and path being the variable
  let source = globalVars.find(
    (param: IImmutableMap<ParamOrVar>) =>
      param
        .get('name')
        .trim()
        .toLowerCase() === path.toLowerCase()
  );
  let value: string;
  //if path has mathced key wi./w.o. dot notation
  if (source !== undefined) {
    value = source.get('value');
  } else {
    //else handle with dedicated method
    const keyAndVar = handleVariableIndex(path);
    const headPath = keyAndVar.key;
    source = globalVars.find(
      (param: IImmutableMap<ParamOrVar>) =>
        param
          .get('name')
          .trim()
          .toLowerCase() === headPath.toLowerCase()
    );
    value = source && source.get('value') ? source.get('value') : null;
    if (keyAndVar.variable && value) {
      value = readValue(keyAndVar.variable, value);
    }
  }
  return value;
};

export interface IReplaceVariables {
  conditionExpression: string;
  paramErrorMessages: string[];
}

export const findAndReplaceVariables = (
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  condition: string,
  RegExpForParamOrVar: RegExp,
  doubleBrace?: boolean,
  tempVars?: IImmutableList<IImmutableMap<ParamOrVar>>
): IReplaceVariables => {
  const paramErrorMessages: string[] = [];
  let conditionExpression = condition;
  const openingBrace = doubleBrace ? '{{' : '{';
  const closingBrace = doubleBrace ? '}}' : '}';
  const regExp = RegExpForParamOrVar;
  if (!!conditionExpression) {
    const condVars = conditionExpression.match(regExp);
    if (condVars && condVars.length > 0) {
      condVars.forEach((condvar: string) => {
        const originalCondVar = condvar;
        condvar = condvar.replace(openingBrace, '').replace(closingBrace, '');
        let variableName = condvar;
        let matchingVar;

        if (tempVars) {
          matchingVar = tempVars.find((param: IImmutableMap<ParamOrVar>) => {
            return (
              param.get('name') &&
              variableName &&
              variableName.toLowerCase() ===
                param
                  .get('name')
                  .trim()
                  .toLowerCase()
            );
          });
        }

        if (!matchingVar) {
          matchingVar = globalVars.find((param: IImmutableMap<ParamOrVar>) => {
            return (
              param.get('name') &&
              variableName &&
              variableName.toLowerCase() ===
                param
                  .get('name')
                  .trim()
                  .toLowerCase()
            );
          });
        }

        if (!!matchingVar) {
          let value = matchingVar.get('value');
          conditionExpression = conditionExpression.replace(originalCondVar, value);
        } else {
          //delimited by '.' read value iteratively
          let jPath = false;
          let indexOfFirstDotDelimiter = -1;
          let indexOfFirstArrayDelimiter = -1;

          if (hasDotDelimiter(condvar)) {
            indexOfFirstDotDelimiter = condvar.indexOf('.');
          }
          if (hasArrayDelimiter(condvar)) {
            indexOfFirstArrayDelimiter = condvar.indexOf('[');
          }
          if (
            (indexOfFirstDotDelimiter !== -1 &&
              indexOfFirstArrayDelimiter !== -1 &&
              indexOfFirstDotDelimiter < indexOfFirstArrayDelimiter) ||
            (indexOfFirstDotDelimiter !== -1 && indexOfFirstArrayDelimiter === -1)
          ) {
            variableName = condvar.split('.')[0];
            // +1 for .
            condvar = condvar.slice(condvar.indexOf('.') + 1, condvar.length);
            jPath = true;
          } else if (indexOfFirstArrayDelimiter !== -1) {
            variableName = condvar.split('[')[0];
            condvar = condvar.slice(condvar.indexOf('['), condvar.length);
            jPath = true;
          }
          matchingVar = globalVars.find((param: IImmutableMap<ParamOrVar>) => {
            return (
              param.get('name') &&
              variableName &&
              variableName.toLowerCase() ===
                param
                  .get('name')
                  .trim()
                  .toLowerCase()
            );
          });
          if (!!matchingVar) {
            let value = matchingVar.get('value');
            let jPathValue;
            if (jPath) {
              // remove the global variable name
              jPathValue = readValue(condvar, value);
            }
            const conditionValue = jPathValue !== undefined ? jPathValue : '';
            conditionExpression = conditionExpression.replace(originalCondVar, conditionValue);
          } else {
            paramErrorMessages.push(`unable to find variable from global state - ${originalCondVar}`);
          }
        }
      });
    } else {
      paramErrorMessages.push(`regex failure: incorrect paramter format - ${conditionExpression}`);
    }
  }
  return { conditionExpression, paramErrorMessages };
};

export const setSignInNode = (
  node: IImmutableMap<GraphNode>,
  isUserSignedIn: IsUserSignedInHandlerType,
  jsonPayload: IImmutableMap<JsonDocument>
): IImmutableMap<GraphNode> => {
  if (node) {
    if (node.get('staticChoices')) {
      let signInTargetNodeId = '';
      node.get('staticChoices').find((choice: IImmutableMap<Choice>) => {
        if (hasSignedIn(choice.get('raiseEventName'), isUserSignedIn)) {
          signInTargetNodeId = choice.get('targetNodeId');
          return true;
        }
        return false;
      });
      if (signInTargetNodeId) {
        const parentId = node.get('parentUId');
        node = jsonPayload.get('nodes').find((n: IImmutableMap<GraphNode>) => n.get('id') === signInTargetNodeId);
        node = node.set('parentUId', parentId);
      }
    }
  }
  return node;
};

export const hasSignedIn = (raiseEventName: string, isUserSignedIn: IsUserSignedInHandlerType) => {
  if (!!raiseEventName && raiseEventName === 'signin') {
    if (isUserSignedIn && typeof isUserSignedIn === 'function') {
      return isUserSignedIn();
    }
  }
  return false;
};

export const wireupLinks = (htmlString: string): string => {
  try {
    if (htmlString) {
      const elm = new DOMParser().parseFromString(htmlString, 'text/html');
      if (!!elm) {
        const anchors: HTMLCollection = elm.getElementsByTagName('a');
        const _count = anchors.length;
        let anchor: HTMLAnchorElement;
        for (let i = 0; i < _count; i++) {
          anchor = anchors.item(i) as HTMLAnchorElement;
          if (anchor && !anchor.hasAttribute('target')) {
            anchor.setAttribute('target', '_blank');
          }
        }
        const firstChild = !!elm.firstElementChild ? elm.firstElementChild : <HTMLElement>elm.firstChild;
        if(firstChild){
          let innerHTML = firstChild.innerHTML;
          const body = firstChild.querySelector('body');
          // remove head and body tags
          if(body){
            innerHTML = body.innerHTML;
          }
          return innerHTML;
        }
      }
    }
  } catch (e) {
    if (e) {
      console.log('wireupLinks failed: ' + e);
    }
  }
  return htmlString;
};

export const addFormFields = (node: IImmutableMap<GraphNode>): IImmutableMap<GraphNode> => {
  const formFields = new FormFieldV2();
  const errorCodeMap = new ErrorCodeMap();
  errorCodeMap.Required = ErrorCodes.DiagnosticsFormRequiredFieldError;
  errorCodeMap.Pattern = ErrorCodes.DiagnosticsFormStringPatternError;
  errorCodeMap.StringLength = ErrorCodes.DiagnosticsFormStringLengthError;
  const constraints = new FormConstraintV2();
  constraints.ErrorMap = createImmutableMap(errorCodeMap);
  const nodeConstraints = node.get('constraints');
  if (nodeConstraints) {
    constraints.Required = nodeConstraints.get('required');
    constraints.MaxLength = nodeConstraints.get('maxLength');
    constraints.MinLength = nodeConstraints.get('minLength');
    constraints.Pattern = nodeConstraints.get('pattern');
    formFields.Constraints = createImmutableMap(constraints);
  }
  formFields.Key = node.get('uId') + 'Input field';
  formFields.Type = FormFieldType.TextField;
  node = node.set('InputFormField', createImmutableMap(formFields));
  return node;
};

export const getNodeLogProperties = (
  parentUId: string,
  node: IImmutableMap<GraphNode>,
  getState: () => YodaState,
  metadata?: IImmutableMap<Meta>
): string => {
  let parentId = 'root';
  let parentName = '';
  if (!!parentUId && parentUId !== 'root') {
    const parentNode = _getNode(parentUId, getState) as IImmutableMap<GraphNode>;
    if (parentNode) {
      parentId = parentNode.get('id');
      parentName = parentNode.get('name');
    }
  }
  const browserTimeStamp = Date.now();
  let nodeData = {
    nodeId: node.get('id') || '',
    nodeName: node.get('name') || '',
    parentId: parentId || '',
    parentName: parentName || '',
    browserTimeStamp: browserTimeStamp || ''
  };

  if (metadata) {
    const dialogInfo = getDialogInfo(metadata);
    nodeData = { ...nodeData, ...dialogInfo };
  }

  return JSON.stringify(nodeData);
};

export const embedVariableInText = (textContent: string, globalVars: IImmutableList<IImmutableMap<ParamOrVar>>) => {
  return findAndReplaceVariables(globalVars, textContent, RegExpForTextContent, true).conditionExpression;
};

export const evalTargetConditions = (globalVars: IImmutableList<IImmutableMap<ParamOrVar>>, condition: string) => {
  const regExp = RegExpForParamOrVar;
  let conditionExpression = findAndReplaceVariables(globalVars, condition, regExp).conditionExpression;
  // eslint-disable-next-line no-eval
  if (eval(conditionExpression)) {
    return true;
  }
  return false;
};

export const getTargetNodeFromCondition = (
  targets: IImmutableList<IImmutableMap<Target>>,
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  targetNodeId: string,
  nodes: IImmutableList<IImmutableMap<GraphNode>>
) => {
  if (targets) {
    const target = targets.find((t: IImmutableMap<Target>) => {
      try {
        return evalTargetConditions(globalVars, t.get('condition'));
      } catch {
        return false;
      }
    });
    if (target) {
      targetNodeId = target.get('targetNodeId');
    }
  }
  return nodes.find((node: IImmutableMap<GraphNode>) => {
    return targetNodeId && node.get('id') && node.get('id').toLowerCase() === targetNodeId.toLowerCase();
  });
};

export const checkNodePreCondition = (
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  node: IImmutableMap<GraphNode>,
  nodes: IImmutableList<IImmutableMap<GraphNode>>,
  preConditionNodes?: IImmutableMap<GraphNode>[]
): any => {
  let defaultNode = null;
  preConditionNodes = preConditionNodes || [];
  if (node && node.get('preCondition') && globalVars) {
    const shouldEvaluateNode = evalTargetConditions(globalVars, node.get('preCondition').get('condition'));
    if (!shouldEvaluateNode) {
      const defaultTargetNodeId = node.get('preCondition').get('targetNodeId');
      defaultNode = getTargetNodeFromCondition(node.get('targets'), globalVars, defaultTargetNodeId, nodes);
      if (defaultNode) {
        if (defaultNode.get('preCondition')) {
          defaultNode = defaultNode.set('skipNode', true);
          preConditionNodes.push(defaultNode);
          checkNodePreCondition(globalVars, defaultNode, nodes, preConditionNodes);
        } else {
          preConditionNodes.push(defaultNode);
        }
      }
    }
  }
  return { node: node, preConditionNodes: preConditionNodes };
};

export const pushPreconditionNodes = (
  preConditionNodes: IImmutableMap<GraphNode>[],
  targetNode: IImmutableMap<GraphNode>,
  mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>,
  dialogId: string
) => {
  targetNode = targetNode.set('skipNode', true);
  mutableRenderedNodes.push(targetNode);
  mutableRenderedNodes;
  if (preConditionNodes) {
    preConditionNodes.forEach((preConditionNode: IImmutableMap<GraphNode>, index: number) => {
      let preConditionTargetNode = updateRenderedTargetNode(
        mutableRenderedNodes,
        preConditionNode,
        targetNode.get('uId'),
        dialogId
      );
      mutableRenderedNodes.push(preConditionTargetNode);
    });
  }
  return mutableRenderedNodes;
};

export const updateRenderedTargetNode = (
  mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>,
  targetNode: IImmutableMap<GraphNode>,
  parentUId: string,
  dialogId: string,
  jsonPayload?: IImmutableMap<JsonDocument>,
  isUserSignedIn?: IsUserSignedInHandlerType
) => {
  // check SignInNode logic
  if (jsonPayload && isUserSignedIn) {
    targetNode = setSignInNode(targetNode, isUserSignedIn, jsonPayload);
  }
  // setEditMode
  let updatedTargetNode = targetNode.withMutations((mutableTargetNode: IImmutableMap<GraphNode>) => {
    mutableTargetNode.set('isInEditMode', true);
  });
  // set UID
  if (!updatedTargetNode.get('uId')) {
    updatedTargetNode = updatedTargetNode.set('uId', `${uuidv4(uuidOptions())}`);
  }
  // Add inputNode formFields
  if (updatedTargetNode.get('type').toLowerCase() === NodeType.InputNode) {
    updatedTargetNode = addFormFields(updatedTargetNode);
  }
  // check if this was a previously rendered dynamic node and copy dynamicChoices
  if (updatedTargetNode.get('dynamicChoices')) {
    const dynamicNodeIdx = mutableRenderedNodes.findLastIndex(
      (n: IImmutableMap<GraphNode>) => n.get('id') === targetNode.get('id')
    );
    if (dynamicNodeIdx && dynamicNodeIdx !== -1) {
      const dynamicNode = mutableRenderedNodes.get(dynamicNodeIdx);
      updatedTargetNode = updatedTargetNode.set('dynamicChoicesList', dynamicNode.get('dynamicChoicesList'));
    }
  }
  // Add parentUId
  updatedTargetNode = updatedTargetNode.set('parentUId', parentUId);
  // Add dialogId
  updatedTargetNode = updatedTargetNode.set('dialogId', dialogId);
  return updatedTargetNode;
};

export const delContextVariables = (yodaContextVariables: IImmutableMap<ParamOrVar>, nodeIndex: number) => {
  //delete the keys larger than nodeIndex when the user edits the choice selection
  if (yodaContextVariables.get('value')) {
    const yodaContextVariablesValues: any = { ...(yodaContextVariables.get('value') as any) };
    // for each context delete the keys larger than current nodeIndex
    Object.keys(yodaContextVariablesValues).forEach(contextKey => {
      //for each yodaContextVariableValue, iterate through the nodeIndex keys inside and delete larger than edited choice nodeIndex
      Object.keys(yodaContextVariablesValues[contextKey]).forEach((nodeIdxKey: string) => {
        if (parseInt(nodeIdxKey) > nodeIndex) {
          yodaContextVariablesValues[contextKey][nodeIdxKey] = null;
          delete yodaContextVariablesValues[contextKey][nodeIdxKey];
        }
      });
    });
    return yodaContextVariables.set('value', yodaContextVariablesValues);
  }
  return yodaContextVariables;
};

export const setContextVariables = (
  adhocVariables: IImmutableList<IImmutableMap<ContextVariableToSet>>,
  yodaContextVariables: IImmutableMap<ParamOrVar>,
  nodeIndex: number
) => {
  const yodaContextVariablesValues: any = yodaContextVariables.get('value');
  //populate adhocVariables values with matched context to yodaContextVariablesValues
  adhocVariables.forEach((adhocVariable: IImmutableMap<ContextVariableToSet>) => {
    const context = adhocVariable.get('context');
    const contextKey = getCaseInsensitiveKey(yodaContextVariablesValues, context);
    let contextVariables = yodaContextVariablesValues[contextKey];
    if (!contextVariables) {
      yodaContextVariablesValues[contextKey] = {};
    }
    if (nodeIndex !== undefined && !yodaContextVariablesValues[contextKey][nodeIndex]) {
      yodaContextVariablesValues[contextKey][nodeIndex] = {};
    }
    const variables = adhocVariable.get('variables');
    variables.forEach((variable: IImmutableMap<AdhocVariableToSet>) => {
      yodaContextVariablesValues[contextKey][nodeIndex][variable.get('key')] = variable.get('value');
    });
  });
  return yodaContextVariables.set('value', yodaContextVariablesValues);
};

const checkForSafResponseValueRead = (
  path: string,
  actionProvider: SAFActionProvider,
  safResponse: ISAFActionExecutionResponse
) => {
  let responseValue: any = safResponse;
  if (path) {
    // know me check
    if (
      actionProvider &&
      actionProvider.toLowerCase() === SAFActionProvider.KnowMe.toLowerCase() &&
      path.startsWith('Value.')
    ) {
      path = path.replace('Value.', '');
      responseValue = safResponse.Value;
    }
    // SAF v1 check
    if (!actionProvider || actionProvider === SAFActionProvider.SAF) {
      // v1
      if (path.startsWith('context.')) {
        path = path.replace('context.', '');
        responseValue = safResponse.context;
      }
      // v2
      if (path.startsWith('Value.context.')) {
        path = path.replace('Value.context.', '');
        responseValue = safResponse.context;
      }
    }
  }
  return { path, responseValue };
};

const checkForDafResponseValueRead = (path: string, dafResponse: any) => {
  let responseValue: any = null;
  if (path) {
    if (path && path.startsWith('Results.') && dafResponse.Results) {
      path = path.replace('Results.', '');
      const keyAndVar = handleVariableIndex(path);
      const headPath = keyAndVar.key;
      const source = dafResponse.Results.find((result: ParamOrVar) => {
        return result.name.trim().toLowerCase() === headPath.toLowerCase();
      });
      let value = source && source.value ? source.value : null;
      if (keyAndVar.variable && value) {
        responseValue = readValue(keyAndVar.variable, value);
      } else if (!keyAndVar.variable && value) {
        responseValue = value;
      }
    } else {
      responseValue = readValue(path, dafResponse);
    }
  }
  return responseValue;
};

export const updateAdhocVariable = (
  variable: IImmutableMap<AdhocVariableToSet>,
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  selectedChoice?: IImmutableMap<Choice>,
  safResponse?: ISAFActionExecutionResponse,
  actionProvider?: SAFActionProvider,
  dafResponse?: DiagnosticsActionResult
) => {
  if (variable) {
    const provider = variable.get('provider') || '';
    const defaultVal = variable.get('default');
    let targetVal: any;
    if (provider) {
      switch (provider.toLowerCase()) {
        case IVariableProvider.HardCoded.toLowerCase():
          return variable.set('value', defaultVal);
        case IVariableProvider.ChoiceText.toLowerCase():
          targetVal = selectedChoice ? selectedChoice.get('text').get('content') : undefined;
          return variable.set('value', targetVal !== undefined && targetVal !== null ? targetVal : defaultVal);
        case IVariableProvider.ChoiceId.toLowerCase():
          targetVal = selectedChoice ? selectedChoice.get('id') : undefined;
          return variable.set('value', targetVal !== undefined && targetVal !== null ? targetVal : defaultVal);
        case IVariableProvider.GlobalVars.toLowerCase():
          targetVal = readValueFromGlobalVars(globalVars, variable.get('path'));
          return variable.set('value', targetVal !== undefined && targetVal !== null ? targetVal : defaultVal);
        case IVariableProvider.Response.toLowerCase():
          let path = variable.get('path');
          if (safResponse) {
            const safResponseRead = checkForSafResponseValueRead(path, actionProvider, safResponse);
            targetVal = readValue(safResponseRead.path, safResponseRead.responseValue);
          } else if (dafResponse) {
            targetVal = checkForDafResponseValueRead(path, dafResponse);
          }
          return variable.set('value', targetVal !== undefined && targetVal !== null ? targetVal : defaultVal);
        default:
          return variable;
      }
    }
  }
  return variable;
};

export const updateAdhocVariableValues = (params: IUpdateAdhocVariablesParams) => {
  const { adhocVariables, globalVars, selectedChoice, safResponse, actionProvider, dafResponse } = params;
  const updatedAdhocVars = adhocVariables
    .map((adhocVariable: IImmutableMap<ContextVariableToSet>) => {
      const variables = adhocVariable.get('variables');
      let updatedVariables: IImmutableMap<AdhocVariableToSet>[] = [];
      variables
        .map((variable: IImmutableMap<AdhocVariableToSet>) => {
          updatedVariables.push(
            updateAdhocVariable(variable, globalVars, selectedChoice, safResponse, actionProvider, dafResponse)
          );
        })
        .toArray();
      return adhocVariable.set('variables', createImmutableList(updatedVariables));
    })
    .toArray();
  return createImmutableList(updatedAdhocVars);
};

export const convertGlobalVarsToObj = (globalVars: IImmutableList<IImmutableMap<ParamOrVar>>) => {
  const results: any = {};
  if (globalVars) {
    globalVars.forEach((globalVar: IImmutableMap<ParamOrVar>) => {
      if (globalVar.get('name') !== YodaContextVariablesKey) {
        results[globalVar.get('name')] = globalVar.get('value');
      }
    });
  }
  return results;
};

export const getCaseInsensitiveKey = (obj: any, key: string) => {
  let newKey;
  if (obj && key) {
    newKey = Object.keys(obj).find((k: string) => {
      if (obj.hasOwnProperty(k)) {
        return k && k.toLowerCase() === key.toLowerCase();
      }
      return false;
    });
  }
  return newKey || key;
};

export const getContextVars = (
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  context: string,
  contextKeyValueLimit?: number
) => {
  let results: IContextMetadata[] = [];
  let keysAdded: string[] = [];
  if (globalVars) {
    const yodaContextVars = globalVars.find((globalVar: IImmutableMap<ParamOrVar>) => {
      return globalVar.get('name') === YodaContextVariablesKey;
    });
    if (!!yodaContextVars) {
      let values: any = yodaContextVars.get('value');
      const contextKey = getCaseInsensitiveKey(values, context);
      values = values ? values[contextKey] : null;
      if (values) {
        Object.keys(values).forEach((index: any) => {
          if (values.hasOwnProperty(index)) {
            const indexContextValues = values[index];
            if (indexContextValues) {
              Object.keys(indexContextValues).forEach(key => {
                if (indexContextValues.hasOwnProperty(key)) {
                  let truncatedKey = key;
                  let truncatedValue = indexContextValues[key];
                  if (contextKeyValueLimit !== undefined) {
                    truncatedKey = key.length > contextKeyValueLimit ? key.slice(0, contextKeyValueLimit) : key;
                    truncatedValue =
                      typeof truncatedValue === 'string' && truncatedValue.length > contextKeyValueLimit
                        ? truncatedValue.slice(0, contextKeyValueLimit)
                        : truncatedValue;
                  }
                  if(truncatedValue && typeof truncatedValue === 'string'){
                    const condVars = truncatedValue.match(RegExpForParamOrVar)
                    if(condVars && condVars.length > 0){
                      truncatedValue = '';
                    }
                  }
                  const metadata: IContextMetadata = {
                    key: truncatedKey,
                    value: typeof truncatedValue !== 'string' ? JSON.stringify(truncatedValue) : truncatedValue
                  };
                  // overwrite key if another node has reset the adhocVariable
                  if (keysAdded.includes(key)) {
                    let idx = results.findIndex((meta: IContextMetadata) => {
                      meta.key === key;
                    });
                    if (idx !== -1) {
                      results[idx] = metadata;
                    }
                  } else {
                    keysAdded.push(key);
                    results.push(metadata);
                  }
                }
              });
            }
          }
        });
      }
    }
  }
  return results;
};
